import { memoize } from "lodash";

// Available in all supported browsers; not yet in this project's TS lib target.
declare global {
  namespace Intl {
    function supportedValuesOf(key: "timeZone"): string[];
  }
}

export type TimezoneOption = {
  /** IANA zone id — the value persisted on the occurrence. */
  timeZone: string;
  city: string;
  country?: string;
  countryCode?: string;
  /** Generic zone name, e.g. "Central European Time" regardless of DST. */
  zoneName: string;
  /** Current UTC offset, e.g. "GMT+02:00". */
  offsetLabel: string;
  offSetInMinutes: number;
  /** Committed display label, e.g. "Budapest, Hungary — Central European Summer Time (GMT+02:00)". */
  formatted: string;
  /** Lowercased haystack for keyword search; includes the generic, standard and DST zone names. */
  searchText: string;
};

const timeZoneNamePart = (timeZone: string, style: "long" | "longGeneric" | "longOffset", date: Date): string =>
  new Intl.DateTimeFormat("en", { timeZone, timeZoneName: style })
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName")?.value ?? "";

const offsetLabelToMinutes = (label: string): number => {
  const match = label.match(/GMT([+-])(\d{2}):(\d{2})/);
  if (!match) return 0;
  const sign = match[1] === "-" ? -1 : 1;
  return sign * (Number(match[2]) * 60 + Number(match[3]));
};

type LocaleWithTimeZones = Intl.Locale & { getTimeZones?: () => string[]; timeZones?: string[] };

const regionTimeZones = (region: string): string[] => {
  try {
    const locale = new Intl.Locale(`und-${region}`) as LocaleWithTimeZones;
    // The Locale Info proposal changed shape mid-flight: older engines expose a
    // `timeZones` accessor, newer ones a `getTimeZones()` method.
    return locale.getTimeZones?.() ?? locale.timeZones ?? [];
  } catch {
    return [];
  }
};

/**
 * Zone → country, derived in the browser by inverting Intl.Locale's region →
 * zones mapping over every ISO region code. Engines without the Locale Info
 * API yield an empty map: options then simply carry no country label.
 */
const buildZoneToCountry = (): Map<string, { name: string; code: string }> => {
  const map = new Map<string, { name: string; code: string }>();
  let displayNames: Intl.DisplayNames;
  try {
    displayNames = new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    return map;
  }
  for (let first = 65; first <= 90; first++) {
    for (let second = 65; second <= 90; second++) {
      const code = String.fromCharCode(first) + String.fromCharCode(second);
      let name: string | undefined;
      try {
        name = displayNames.of(code);
      } catch {
        continue;
      }
      // DisplayNames echoes back codes it does not recognize.
      if (!name || name === code || name === "Unknown Region") continue;
      for (const zone of regionTimeZones(code)) {
        if (!map.has(zone)) map.set(zone, { name, code });
      }
    }
  }
  return map;
};

export const listOfTimezoneOptions = memoize((): readonly TimezoneOption[] => {
  const zoneToCountry = buildZoneToCountry();
  const now = new Date();
  const year = now.getFullYear();
  // A zone's long name differs between standard time and DST; indexing the
  // January and July names makes both findable year-round.
  const january = new Date(year, 0, 1);
  const july = new Date(year, 6, 1);

  const options = Intl.supportedValuesOf("timeZone").map((timeZone): TimezoneOption => {
    const rawOffset = timeZoneNamePart(timeZone, "longOffset", now);
    const offsetLabel = rawOffset === "GMT" ? "GMT+00:00" : rawOffset;
    const januaryName = timeZoneNamePart(timeZone, "long", january);
    const julyName = timeZoneNamePart(timeZone, "long", july);
    // Zones without a metazone get an offset-like generic name; the specific
    // long name reads better there (e.g. UTC → "Coordinated Universal Time").
    const genericName = timeZoneNamePart(timeZone, "longGeneric", now);
    const zoneName =
      genericName && !genericName.startsWith("GMT") ? genericName : timeZoneNamePart(timeZone, "long", now);
    const city = timeZone.split("/").pop()?.replace(/_/g, " ") ?? timeZone;
    const country = zoneToCountry.get(timeZone);
    const formatted = `${country ? `${city}, ${country.name}` : city} — ${zoneName} (${offsetLabel})`;

    return {
      timeZone,
      city,
      country: country?.name,
      countryCode: country?.code,
      zoneName,
      offsetLabel,
      offSetInMinutes: offsetLabelToMinutes(offsetLabel),
      formatted,
      // formatted is indexed too so a partially backspaced committed label
      // (punctuation included) still matches its own zone.
      searchText: [city, country?.name, zoneName, januaryName, julyName, timeZone, offsetLabel, formatted]
        .filter(Boolean)
        .join(" ")
        .toLowerCase(),
    };
  });

  options.sort((a, b) => a.offSetInMinutes - b.offSetInMinutes || a.city.localeCompare(b.city));
  options.forEach((option) => Object.freeze(option));
  return Object.freeze(options);
});

/**
 * Keyword search over the full option list: matches anywhere within the city,
 * country, zone name (standard or DST), zone id, or offset. Queries containing
 * a digit also match offsets loosely, so "utc-5", "gmt -5", "-05:00" and "-5"
 * all reach the same rows.
 */
export const searchTimezones = (query: string): TimezoneOption[] => {
  const options = listOfTimezoneOptions();
  const q = query.trim().toLowerCase();
  if (!q) return [...options];
  const numeric = q.replace(/\s+/g, "").replace(/^(utc|gmt)/, "");
  const isOffsetQuery = /\d/.test(numeric);
  return options.filter((option) => {
    if (option.searchText.includes(q)) return true;
    if (!isOffsetQuery) return false;
    const offset = option.offsetLabel.toLowerCase().replace("gmt", "");
    const shortOffset = offset.replace(":00", "").replace(/([+-])0(\d)/, "$1$2");
    return offset.includes(numeric) || shortOffset === numeric;
  });
};

export const findTimezoneByZone = (timeZone: string): TimezoneOption | undefined =>
  listOfTimezoneOptions().find((option) => option.timeZone === timeZone);

/**
 * Best-effort match for contexts that only know a UTC offset (e.g. a picked
 * address): a zone in the given country takes priority over the first zone
 * sharing the offset.
 */
export const findTimezoneByOffset = (offSetInMinutes: number, countryCode?: string): TimezoneOption | undefined => {
  const sameOffset = listOfTimezoneOptions().filter((option) => option.offSetInMinutes === offSetInMinutes);
  if (countryCode) {
    const inCountry = sameOffset.find((option) => option.countryCode === countryCode.toUpperCase());
    if (inCountry) return inCountry;
  }
  return sameOffset[0];
};
